import { translate, TranslationEntity } from '@repo/utils';
import { type Node, type XYPosition } from '@xyflow/react';
import type { MenuProps } from 'antd';
import { Menu } from 'antd';
import { NodeType } from 'automations/data/nodeTypes';
import { useAutomationDispatch } from 'automations/hooks/useAutomationDispatch';
import { replaceFallbackNode } from 'automations/store/reactFlowSlice';
import { ActionTypes, ConditionTypes } from 'automations/types/automationConfig';
import { useCallback } from 'react';

import styles from './CreateAutomationNodeMenu.module.scss';

type MenuItem = Required<MenuProps>['items'][number];
type MenuItemWithChildren = MenuItem & { children?: MenuItem[] };

const menuItems: MenuItemWithChildren[] = [
  {
    key: NodeType.condition,
    label: 'Conditions',
    children: [
      { key: ConditionTypes.ifElse, label: 'If / Else' },
      { key: ConditionTypes.wait, label: 'Wait' },
      { key: ConditionTypes.multiIfElse, label: 'Multi If / Else' },
      { key: ConditionTypes.compare, label: 'Compare' },
    ],
  },
  {
    key: NodeType.action,
    label: 'Actions',
    children: [
      {
        key: 'leadActions',
        label: translate(`#{${TranslationEntity.lead}} Actions`, [{
          capitalize: true,
        }]),
        children: [
          {
            key: ActionTypes.updateLead,
            label: translate(`Update #{${TranslationEntity.lead}}`, [{
              capitalize: true,
            }]),
          },
          {
            key: ActionTypes.addLeadActivity,
            label: translate(`Add #{${TranslationEntity.lead}} #{${TranslationEntity.activity}}`, [{
              capitalize: true,
            },
            {
              capitalize: true,
            }]),
          },
        ],
      },
      {
        key: 'salesExecution',
        label: 'Sales Execution',
        children: [
          {
            key: ActionTypes.distributeLead,
            label: translate(`Distribute #{${TranslationEntity.lead}}`, [{
              capitalize: true,
            }]),
          },
          {
            key: ActionTypes.createTask,
            label: translate(`Create #{${TranslationEntity.task}}`, [{
              capitalize: true,
            }]),
          },
          {
            key: ActionTypes.completeTask,
            label: translate(`Complete #{${TranslationEntity.task}}`, [{
              capitalize: true,
            }]),
          },
        ],
      },
      {
        key: 'messaging',
        label: 'Messaging',
        children: [
          { key: ActionTypes.sendEmail, label: 'Send Email' },
        ],
      },
      {
        key: 'customActions',
        label: 'Custom Actions',
        children: [
          { key: ActionTypes.webhook, label: 'Webhook' },
          { key: ActionTypes.sendWebengageEvent, label: 'Send WebEngage Event' },
          { key: ActionTypes.triggerAiCall, label: 'Trigger AI Call' },
        ],
      },
    ],
  },
  // add other menu items here
  // sub automation is disabled for now
  // {
  //   type: 'divider',
  //   key: 'divider2',
  // },
  // {
  //   key: 'sendToSubAutomation',
  //   label: 'Send to Sub-Automation',
  //   icon: <SettingOutlined />,
  // },
];

export default function CreateAutomationNodeMenu({
  handleVisibleChange,
  nodeId,
  defaultXYPosition,
  currentData,
}: {
  handleVisibleChange: (visible: boolean) => void;
  nodeId: string;
  defaultXYPosition: XYPosition;
  currentData: any;
}) {
  const dispatch = useAutomationDispatch();

  const handleMenuItemClick = useCallback((info: { key: string, keyPath: string[] }) => {
    const newNode: Node<any> = {
      id: nodeId,
      type: info.keyPath[info.keyPath.length - 1] as NodeType,
      position: defaultXYPosition,
      data: {
        executionOrder: currentData?.executionOrder,
        nodeSubType: info.key,
        menuOpen: true,
      },
    };

    dispatch(replaceFallbackNode({ nodeId, newNode }));

    handleVisibleChange(false);
  }, [currentData, defaultXYPosition, dispatch, handleVisibleChange, nodeId]);

  return (
    <Menu
      className={styles.popover}
      defaultOpenKeys={[NodeType.condition]}
      items={menuItems}
      onClick={handleMenuItemClick}
    />
  );
}
